import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';

export type ManualResumen = components['schemas']['ManualSummary'];
export type Manual = components['schemas']['ManualDetail'];
export type PaginaManual = components['schemas']['ManualPage'];

const clave = (id: number) => ['contenido', 'manual', id];
const ruta = (id: number) => ({ params: { path: { id: String(id) } } });

export function useManuales() {
  return useQuery({
    queryKey: ['contenido', 'manuales'],
    queryFn: () => unwrap<ManualResumen[]>(api.GET('/api/v1/admin/manuals')),
  });
}

export function useManual(id: number | null) {
  return useQuery({
    queryKey: clave(id ?? 0),
    queryFn: () => unwrap<Manual>(api.GET('/api/v1/admin/manuals/{id}', ruta(id ?? 0))),
    enabled: id !== null,
  });
}

export function useManualMutaciones(id: number) {
  const qc = useQueryClient();
  const err = (e: unknown) => toast.error(mensajeDe(e));
  const poner = (m: Manual) => {
    qc.setQueryData(clave(id), m);
    void qc.invalidateQueries({ queryKey: ['contenido', 'manuales'] });
  };
  const hijo = (childId: number) => ({
    params: { path: { id: String(id), childId: String(childId) } },
  });
  return {
    agregar: useMutation({
      mutationFn: (body: components['schemas']['ManualPagesAddBody']) =>
        unwrap<Manual>(api.POST('/api/v1/admin/manuals/{id}/pages', { ...ruta(id), body })),
      onSuccess: poner,
      onError: err,
    }),
    editar: useMutation({
      mutationFn: ({
        childId,
        body,
      }: {
        childId: number;
        body: components['schemas']['ManualPageUpdateBody'];
      }) =>
        unwrap<Manual>(
          api.PATCH('/api/v1/admin/manuals/{id}/pages/{childId}', { ...hijo(childId), body }),
        ),
      onSuccess: poner,
      onError: err,
    }),
    borrar: useMutation({
      mutationFn: (childId: number) =>
        unwrap<Manual>(api.DELETE('/api/v1/admin/manuals/{id}/pages/{childId}', hijo(childId))),
      onSuccess: poner,
      onError: err,
    }),
    ordenar: useMutation({
      mutationFn: (ids: number[]) =>
        unwrap<Manual>(
          api.PUT('/api/v1/admin/manuals/{id}/pages/order', { ...ruta(id), body: { ids } }),
        ),
      onSuccess: poner,
      onError: err,
    }),
  };
}
